import { Dispatch, SetStateAction, useCallback, useState, useSyncExternalStore } from 'react';

const MISSING = Symbol('missing');

const listeners = new Map<string, Set<() => void>>();
const parsed = new Map<string, { raw: string; value: unknown }>();

const notify = (key: string | null) => {
    if (key === null) listeners.forEach((set) => set.forEach((listener) => listener()));
    else listeners.get(key)?.forEach((listener) => listener());
};

const onStorage = (event: StorageEvent) => {
    if (event.storageArea === localStorage || event.storageArea === null) notify(event.key);
};

const subscribe = (key: string, listener: () => void) => {
    if (listeners.size === 0) window.addEventListener('storage', onStorage);
    const set = listeners.get(key) ?? new Set();
    listeners.set(key, set);
    set.add(listener);
    return () => {
        set.delete(listener);
        if (set.size === 0) listeners.delete(key);
        if (listeners.size === 0) window.removeEventListener('storage', onStorage);
    };
};

const readRaw = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

const parse = (key: string, raw: string | null): unknown => {
    if (!raw) return MISSING;
    if (raw === 'undefined') return undefined;
    const hit = parsed.get(key);
    if (hit?.raw === raw) return hit.value;
    let value: unknown;
    try {
        value = JSON.parse(raw);
    } catch {
        return MISSING;
    }
    parsed.set(key, { raw, value });
    return value;
};

const readValue = <T>(key: string, fallback: T): T => {
    const value = parse(key, readRaw(key));
    return value === MISSING ? fallback : (value as T);
};

const write = (key: string, raw: string | null) => {
    try {
        if (raw === null) localStorage.removeItem(key);
        else localStorage.setItem(key, raw);
    } catch {
        return;
    }
    notify(key);
};

const getServerSnapshot = () => null;

/**
 * Состояние в localStorage, общее для хуков с тем же ключом во вкладке и между вкладками.
 * Формат и чтение — как у прежнего `usehooks-ts`: запись `JSON.stringify`, `"undefined"` читается как `undefined`,
 * пустое или битое содержимое — как отсутствие ключа. Значение из хранилища — с первого рендера; `defaultValue`
 * в хранилище не пишется, берётся на первом рендере ключа и подставляется, пока ключа нет. Разобранное значение
 * общее для всех хуков ключа, пока не изменилась строка в хранилище: ссылка результата стабильна, монтирование
 * хука ничего не пишет и соседей не перерисовывает.
 */
export const useLocalStorageState = <T>(key: string, defaultValue: T): [T, Dispatch<SetStateAction<T>>, () => void] => {
    const [fallback, setFallback] = useState({ key, value: defaultValue });
    if (fallback.key !== key) setFallback({ key, value: defaultValue });
    const fallbackValue = fallback.key === key ? fallback.value : defaultValue;

    const subscribeKey = useCallback((listener: () => void) => subscribe(key, listener), [key]);
    const raw = useSyncExternalStore(subscribeKey, () => readRaw(key), getServerSnapshot);
    const value = parse(key, raw);

    const setValue = useCallback<Dispatch<SetStateAction<T>>>(
        (next) => {
            const resolved = next instanceof Function ? next(readValue(key, fallbackValue)) : next;
            write(key, JSON.stringify(resolved));
        },
        [key, fallbackValue],
    );

    const remove = useCallback(() => write(key, null), [key]);

    return [value === MISSING ? fallbackValue : (value as T), setValue, remove];
};
